import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Define types
interface Event {
  id: number;
  name: string;
  location: string;
  performance_time: string;
  end_time?: string;
  livestream?: boolean | number;
  currently_performing?: boolean;
  category?: string;
  description?: string;
  waiting?: number;
}

interface EventStats {
  likePct: number;
  neuPct: number;
  disPct: number;
}

interface RatingsResponse {
  buckets: { like: number; neutral: number; dislike: number };
  reactions: Record<string, number>;
}

interface HistoryPoint {
  time_interval: string;
  rating: string;
  bucket: string;
  count: number;
}

// Reaction definitions
const REACTIONS = {
  // Upvote
  fire:         { emoji: '🔥', label: 'FIRE',         bucket: 'like' },
  transcendent: { emoji: '🤯', label: 'TRANSCENDENT', bucket: 'like' },
  crying:       { emoji: '😭', label: 'CRYING',       bucket: 'like' },
  mainstage:    { emoji: '🎡', label: 'MAIN STAGE',   bucket: 'like' },
  // Neutral
  mid:          { emoji: '😐', label: 'MID',           bucket: 'neutral' },
  beerrun:      { emoji: '🍺', label: 'BEER RUN',     bucket: 'neutral' },
  phone:        { emoji: '📱', label: 'ON MY PHONE',   bucket: 'neutral' },
  // Downvote
  walk:         { emoji: '🚶', label: 'WALK',          bucket: 'dislike' },
  dead:         { emoji: '💀', label: 'DEAD',          bucket: 'dislike' },

  asleep:       { emoji: '😴', label: 'ASLEEP',        bucket: 'dislike' },
} as const;

// Stage-specific reactions (key → { emoji, label, bucket, stage })
const STAGE_REACTIONS: Record<string, { emoji: string; label: string; bucket: string; stage: string }> = {
  yuMAHgod:  { emoji: '😱', label: 'Yu-MAH GOD',  bucket: 'like',    stage: 'Yuma' },
  quastar:   { emoji: '⭐', label: 'Qua-STAR',    bucket: 'like',    stage: 'Quasar' },
  yooma:     { emoji: '🙌', label: 'Yoo!ma',       bucket: 'like',    stage: 'Yuma' },
  goodbye:   { emoji: '🚪', label: 'Go-BYE',      bucket: 'dislike', stage: 'Gobi' },
  yumeh:     { emoji: '😐', label: 'Yu-MEH',      bucket: 'neutral', stage: 'Yuma' },
};

type ReactionKey = keyof typeof REACTIONS;

const BUCKET_COLORS: Record<string, string> = {
  like: 'bg-green-500',
  neutral: 'bg-yellow-500',
  dislike: 'bg-red-500',
};

const BUCKET_BORDERS: Record<string, string> = {
  like: 'border-green-500/40 hover:border-green-400',
  neutral: 'border-yellow-500/40 hover:border-yellow-400',
  dislike: 'border-red-500/40 hover:border-red-400',
};

// YouTube livestream links per stage (Weekend 2)
const YT_LINKS: Record<string, string> = {
  'Main Stage':       'https://www.youtube.com/watch?v=xIiSaqq10Yw',
  'Outdoor Theatre':  'https://www.youtube.com/watch?v=CzAJcgiywFo',
  'Sahara':           'https://www.youtube.com/watch?v=bkCTPTwU7ls',
  'Mojave':           'https://www.youtube.com/watch?v=cYT79Dp0Wuk',
  'Gobi':             'https://www.youtube.com/watch?v=OMImphySFJs',
  'Yuma':             'https://www.youtube.com/watch?v=s5A5Y93nmPI',
  'Quasar':           'https://www.youtube.com/watch?v=iulgXa5S62A',
};

// API URL
const API_URL = process.env.REACT_APP_API_URL || 'https://events.b2b2b.net';

// Styling
const neonStyle = {
  color: '#a9fefd',
  textShadow: [
    '0 0 2px #a9fefd',
    '0 0 6px #00d8e9',
    '0 0 10px #00b3c6'
  ].join(', ')
};

const redNeon = {
  color: '#ff6b6b',
  textShadow: '0 0 4px #ff6b6b, 0 0 10px #ff3b3b, 0 0 18px #c40000'
};

const purpleNeon = {
  color: '#bf9aff',
  textShadow: '0 0 4px #bf9aff, 0 0 10px #7f3afd, 0 0 18px #6411f2'
};

const VOTE_COOLDOWN_MS = 1_000;

// Reaction Buttons Component
function ReactionButtons({ onVote, stage }: { onVote: (rating: string) => void; stage?: string }) {
  const [lastVoteAt, setLastVoteAt] = useState(() => Number(localStorage.getItem('lastVoteAt')) || 0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const rem = Math.max(0, VOTE_COOLDOWN_MS - (now - lastVoteAt));
      setCooldownRemaining(rem);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [lastVoteAt]);

  const handleVote = (rating: string) => {
    const now = Date.now();
    if (now - lastVoteAt < VOTE_COOLDOWN_MS) return;
    onVote(rating);
    setLastVoteAt(now);
    localStorage.setItem('lastVoteAt', String(now));
  };

  const disabled = cooldownRemaining > 0;
  const secondsLeft = Math.ceil(cooldownRemaining / 1000);

  const bucketOrder = ['like', 'neutral', 'dislike'] as const;

  // Get stage-specific reactions for current stage
  const stageExtras = stage
    ? Object.entries(STAGE_REACTIONS).filter(([, r]) => r.stage === stage)
    : [];

  return (
    <div>
      <div className="grid grid-cols-3 gap-3">
        {bucketOrder.map(bucket => {
          const extras = stageExtras.filter(([, r]) => r.bucket === bucket);
          return (
            <div key={bucket} className="space-y-2">
              {Object.entries(REACTIONS)
                .filter(([, r]) => r.bucket === bucket)
                .map(([key, r]) => (
                  <button
                    key={key}
                    onClick={() => handleVote(key)}
                    disabled={disabled}
                    className={`w-full px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all disabled:opacity-40 ${BUCKET_BORDERS[bucket]} bg-slate-800 hover:bg-slate-700 active:scale-95`}
                  >
                    <span className="text-lg">{r.emoji}</span>{' '}
                    <span className="hidden sm:inline">{r.label}</span>
                  </button>
                ))}
              {extras.map(([key, r]) => (
                <button
                  key={key}
                  onClick={() => handleVote(key)}
                  disabled={disabled}
                  className={`w-full px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all disabled:opacity-40 ${BUCKET_BORDERS[bucket]} bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-700 hover:to-slate-600 active:scale-95 ring-1 ring-cyan-500/30`}
                >
                  <span className="text-lg">{r.emoji}</span>{' '}
                  <span className="hidden sm:inline">{r.label}</span>
                </button>
              ))}
            </div>
          );
        })}
      </div>
      {disabled && (
        <p className="text-center text-sm text-gray-400 mt-2">
          Next vote in {secondsLeft}s
        </p>
      )}
    </div>
  );
}

// Helper function to get emoji based on stats
const getPulseEmoji = (stats: EventStats | undefined) => {
  if (!stats) return '';
  const { likePct, neuPct, disPct } = stats;
  if (likePct >= neuPct && likePct >= disPct) return '🔥';
  if (disPct >= likePct && disPct >= neuPct) return '💀';
  return '😐';
};

// Format time diff for "up next" feature
const formatTimeDiff = (diffMs: number) => {
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// Parse bucket stats from the new API response
const parseBucketStats = (data: RatingsResponse): EventStats & { total: number; reactions: Record<string, number> } => {
  const { buckets, reactions } = data;
  const total = (buckets.like + buckets.neutral + buckets.dislike) || 1;
  return {
    likePct: Math.round(buckets.like / total * 100),
    neuPct: Math.round(buckets.neutral / total * 100),
    disPct: Math.round(buckets.dislike / total * 100),
    total: buckets.like + buckets.neutral + buckets.dislike,
    reactions,
  };
};

// Main App Component
const App = () => {
  const getLocalTime = () => {
    const now = new Date();
    const pstIso = now.toLocaleString('sv-SE', { timeZone: 'America/Los_Angeles' }).replace(' ', 'T') + '-07:00';
    return new Date(pstIso);
  };

  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [ratingsData, setRatingsData] = useState<RatingsResponse | null>(null);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [eventRatingsMap, setEventRatingsMap] = useState<Record<number, EventStats>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(getLocalTime());
  const [selectedDay, setSelectedDay] = useState('2026-04-18');
  const [selectedStage, setSelectedStage] = useState('all');
  const [showLate, setShowLate] = useState(false);
  const [showMosaic, setShowMosaic] = useState(false);

  useEffect(() => {
    const iv = setInterval(() => setCurrentTime(getLocalTime()), 60_000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/events`);
        if (!res.ok) throw new Error();
        setEvents(await res.json());
      } catch {
        setError('Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    load();
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, []);

  const currentlyPlaying = useMemo(() => {
    const now = currentTime.getTime();
    const playing = events.filter(ev => {
      const startMs = new Date(ev.performance_time.replace(' ', 'T') + '-07:00').getTime();
      const endMs = ev.end_time
        ? new Date(ev.end_time.replace(' ', 'T') + '-07:00').getTime()
        : startMs + 90 * 60 * 1000;
      return now >= startMs && now < endMs;
    });
    if (!selectedEvent && playing.length) setSelectedEvent(playing[0]);
    return playing;
  }, [events, currentTime, selectedEvent]);

  const waitingEvents = useMemo(() => {
    return events.filter(ev => ev.waiting === 1);
  }, [events]);

  const upcomingEvents = useMemo(() => {
    if (currentlyPlaying.length > 0) return [];
    const now = currentTime.getTime();
    return events
      .filter(ev => {
        const eventDate = ev.performance_time.substring(0, 10);
        const currentDate = currentTime.toISOString().substring(0, 10);
        if (eventDate !== currentDate) return false;
        const startMs = new Date(ev.performance_time.replace(' ', 'T') + '-07:00').getTime();
        return startMs > now;
      })
      .sort((a, b) => {
        const startA = new Date(a.performance_time.replace(' ', 'T') + '-07:00').getTime();
        const startB = new Date(b.performance_time.replace(' ', 'T') + '-07:00').getTime();
        return startA - startB;
      });
  }, [events, currentTime, currentlyPlaying]);

  const nextEvent = useMemo(() => upcomingEvents[0] || null, [upcomingEvents]);

  const timeUntilNext = useMemo(() => {
    if (!nextEvent) return null;
    const diffMs = new Date(nextEvent.performance_time.replace(' ', 'T') + '-07:00').getTime() - currentTime.getTime();
    return formatTimeDiff(diffMs);
  }, [nextEvent, currentTime]);

  // Fetch ratings for currently playing events
  useEffect(() => {
    currentlyPlaying.forEach(ev => {
      fetch(`${API_URL}/api/events/${ev.id}/ratings`)
        .then(r => r.json())
        .then((data: RatingsResponse) => {
          const total = (data.buckets.like + data.buckets.neutral + data.buckets.dislike) || 1;
          setEventRatingsMap(prev => ({
            ...prev,
            [ev.id]: {
              likePct: Math.round(data.buckets.like / total * 100),
              neuPct: Math.round(data.buckets.neutral / total * 100),
              disPct: Math.round(data.buckets.dislike / total * 100),
            }
          }));
        })
        .catch(() => {});
    });
  }, [currentlyPlaying]);

  // Fetch detailed ratings and history for selected event
  useEffect(() => {
    if (!selectedEvent) return;
    const loadDetail = async () => {
      try {
        const [rRes, hRes] = await Promise.all([
          fetch(`${API_URL}/api/events/${selectedEvent.id}/ratings`),
          fetch(`${API_URL}/api/events/${selectedEvent.id}/history`)
        ]);
        if (!rRes.ok || !hRes.ok) throw new Error();
        setRatingsData(await rRes.json());
        setHistory(await hRes.json());
      } catch {
        setError('Failed to load details');
      }
    };
    loadDetail();
    const iv = setInterval(loadDetail, 10_000);
    return () => clearInterval(iv);
  }, [selectedEvent]);

  const submitRating = async (rating: string) => {
    if (!selectedEvent) return;
    try {
      await fetch(`${API_URL}/api/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId: selectedEvent.id, rating })
      });
      const rRes = await fetch(`${API_URL}/api/events/${selectedEvent.id}/ratings`);
      if (rRes.ok) setRatingsData(await rRes.json());
    } catch {
      setError('Failed to submit');
    }
  };

  // Stats from new API format
  const stats = ratingsData ? parseBucketStats(ratingsData) : {
    likePct: 0, neuPct: 0, disPct: 0, total: 0, reactions: {}
  };

  // Chart data — group by bucket
  const chartData = useMemo(() => {
    const acc: Record<string, { time: string; like: number; neutral: number; dislike: number }> = {};
    for (const p of history) {
      if (!acc[p.time_interval]) acc[p.time_interval] = { time: p.time_interval, like: 0, neutral: 0, dislike: 0 };
      const bucket = p.bucket || 'neutral';
      acc[p.time_interval][bucket as 'like' | 'neutral' | 'dislike'] += p.count;
    }
    return Object.values(acc);
  }, [history]);

  const uniqueStages = useMemo(() => Array.from(new Set(events.map(e => e.location))).sort(), [events]);

  const filteredEvents = useMemo(() => {
    return events
      .filter(e => e.performance_time.startsWith(selectedDay))
      .filter(e => selectedStage === 'all' || e.location === selectedStage)
      .filter(e => {
        if (!showLate) return true;
        const startTime = new Date(e.performance_time.replace(' ', 'T') + '-07:00');
        return startTime.getHours() >= 22 || startTime.getHours() < 4;
      })
      .sort((a, b) => {
        const timeA = new Date(a.performance_time.replace(' ', 'T') + '-07:00').getTime();
        const timeB = new Date(b.performance_time.replace(' ', 'T') + '-07:00').getTime();
        return timeA - timeB;
      });
  }, [events, selectedDay, selectedStage, showLate]);

  // Top reactions for the selected event
  const topReactions = useMemo(() => {
    if (!stats.reactions || !stats.total) return [];
    const allReactions = { ...REACTIONS, ...STAGE_REACTIONS };
    return Object.entries(stats.reactions)
      .filter(([key]) => key in allReactions)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([key, count]) => ({
        key,
        emoji: (allReactions as any)[key].emoji,
        label: (allReactions as any)[key].label,
        count,
      }));
  }, [stats]);

  if (loading) return <div className="flex justify-center items-center h-screen text-white text-xl">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-800 text-white p-4">
      <h1 className="text-4xl sm:text-5xl font-bold text-center mb-3" style={neonStyle}>
        Couchella Reactions
      </h1>
      <p className="text-center text-cyan-400 mb-3 text-lg">
        {currentTime.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: true })} PT
      </p>
      <div className="text-center mb-6">
        <button
          onClick={() => setShowMosaic(true)}
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 border border-cyan-500/40 hover:border-cyan-400 rounded-lg text-sm font-medium transition-all inline-flex items-center gap-2"
        >
          📺 Preview All Stages
        </button>
      </div>
      {error && <div className="bg-red-900 px-4 py-2 mb-4 rounded text-center">{error}</div>}

      {/* Mosaic Modal */}
      {showMosaic && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowMosaic(false)}
        >
          <div
            className="max-w-5xl w-full bg-slate-800 rounded-xl border border-slate-600 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-slate-700">
              <h2 className="text-lg font-bold" style={neonStyle}>📺 All Stages — Live Screenshots</h2>
              <button
                onClick={() => setShowMosaic(false)}
                className="text-gray-400 hover:text-white text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            <div className="p-2">
              <img
                src={`${API_URL}/latest.jpg?t=${Math.floor(Date.now() / 30000)}`}
                alt="All stages mosaic"
                className="w-full rounded"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).parentElement!.innerHTML = '<p class="text-center text-gray-400 py-12">No mosaic available yet — screenshots update every minute</p>';
                }}
              />
            </div>
            <div className="px-4 pb-3 text-right">
              <p className="text-xs text-gray-500">Updates every ~60 seconds</p>
            </div>
          </div>
        </div>
      )}

      {/* HAPPENING NOW */}
      {(currentlyPlaying.length > 0 || waitingEvents.length > 0) ? (
        <section className="mb-8">
          <h2 className="text-3xl font-bold text-center mb-4" style={redNeon}>HAPPENING NOW</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {waitingEvents.map(event => (
              <div
                key={`wait-${event.id}`}
                className="p-4 rounded-lg border-2 border-dashed border-yellow-500/60 bg-yellow-500/10 cursor-pointer transition-all hover:border-yellow-400"
                onClick={() => setSelectedEvent(event)}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">⏳</span>
                  <span className="text-yellow-400 text-sm font-semibold uppercase tracking-wide">Waiting for</span>
                </div>
                <h3 className="text-xl font-bold">{event.name}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <p className="text-gray-300 text-sm">{event.location}</p>
                  {event.category && <span className="text-xs bg-slate-600 px-1.5 py-0.5 rounded-full">{event.category}</span>}
                  {event.livestream && YT_LINKS[event.location] ? (
                    <a href={YT_LINKS[event.location]} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-xs bg-red-600 hover:bg-red-500 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1">
                      ▶ WATCH LIVE
                    </a>
                  ) : null}
                </div>
                {event.description && <p className="text-xs text-gray-400 mt-1 italic">{event.description}</p>}
                <p className="text-xs text-yellow-400/70 mt-2">
                  Scheduled: {new Date(event.performance_time.replace(' ', 'T') + '-07:00').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                </p>
              </div>
            ))}
            {currentlyPlaying.map(event => (
              <div
                key={event.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-lg ${
                  selectedEvent?.id === event.id
                    ? 'border-cyan-400 bg-slate-700'
                    : 'border-slate-600 bg-slate-900 hover:border-slate-400'
                }`}
                onClick={() => setSelectedEvent(event)}
              >
                <div className="flex justify-between items-start">
                  <h3 className="text-xl font-bold">{event.name}</h3>
                  {eventRatingsMap[event.id] && (
                    <span className="text-2xl">{getPulseEmoji(eventRatingsMap[event.id])}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <p className="text-gray-300 text-sm">{event.location}</p>
                  {event.category && <span className="text-xs bg-slate-600 px-1.5 py-0.5 rounded-full">{event.category}</span>}
                  {event.livestream && YT_LINKS[event.location] ? (
                    <a href={YT_LINKS[event.location]} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-xs bg-red-600 hover:bg-red-500 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1">
                      ▶ WATCH LIVE
                    </a>
                  ) : event.livestream ? <span className="text-xs bg-red-600 px-1.5 py-0.5 rounded-full font-medium">LIVE</span> : null}
                </div>
                {event.description && <p className="text-xs text-gray-400 mt-1 italic">{event.description}</p>}
                {eventRatingsMap[event.id] && (
                  <div className="mt-3">
                    <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div className="flex h-full">
                        <div className="bg-green-500 transition-all" style={{ width: `${eventRatingsMap[event.id].likePct}%` }} />
                        <div className="bg-yellow-500 transition-all" style={{ width: `${eventRatingsMap[event.id].neuPct}%` }} />
                        <div className="bg-red-500 transition-all" style={{ width: `${eventRatingsMap[event.id].disPct}%` }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ) : nextEvent ? (
        <section className="mb-8">
          <h2 className="text-3xl font-bold text-center mb-4" style={purpleNeon}>UP NEXT</h2>
          <div className="max-w-2xl mx-auto bg-slate-700 p-6 rounded-lg border-2 border-purple-300">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-2xl font-bold">{nextEvent.name}</h3>
                <p className="text-gray-300">{nextEvent.location}</p>
                <p className="text-gray-400 mt-2">
                  {new Date(nextEvent.performance_time.replace(' ', 'T') + '-07:00').toLocaleTimeString([], {
                    hour: '2-digit', minute: '2-digit', hour12: true
                  })}
                </p>
              </div>
              <div className="bg-purple-900 px-4 py-2 rounded-lg text-center">
                <p className="text-purple-200 text-xs font-medium">STARTS IN</p>
                <p className="text-white text-xl font-bold">{timeUntilNext}</p>
              </div>
            </div>
          </div>
        </section>
      ) : currentTime.getHours() >= 1 && (
        <div className="text-center text-2xl mt-12 mb-8" style={redNeon}>
          That's a wrap for tonight — see you tomorrow!
        </div>
      )}

      {/* SELECTED EVENT DETAIL */}
      {selectedEvent && (
        <section className="mb-8 max-w-4xl mx-auto">
          <div className="bg-slate-700 p-6 rounded-lg border border-cyan-800">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold">{selectedEvent.name}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <p className="text-gray-300">{selectedEvent.location}</p>
                  {selectedEvent.category && <span className="text-xs bg-slate-600 px-2 py-0.5 rounded-full">{selectedEvent.category}</span>}
                  {selectedEvent.livestream && YT_LINKS[selectedEvent.location] ? (
                    <a href={YT_LINKS[selectedEvent.location]} target="_blank" rel="noopener noreferrer" className="text-xs bg-red-600 hover:bg-red-500 px-2 py-1 rounded-full font-medium inline-flex items-center gap-1">
                      ▶ WATCH LIVE
                    </a>
                  ) : selectedEvent.livestream ? <span className="text-xs bg-red-600 px-1.5 py-0.5 rounded-full font-medium">LIVE</span> : null}
                </div>
                {selectedEvent.description && <p className="text-sm text-gray-400 mt-1 italic">{selectedEvent.description}</p>}
                <p className="text-sm text-gray-400 mt-1">
                  {new Date(selectedEvent.performance_time.replace(' ', 'T') + '-07:00').toLocaleString([], {
                    weekday: 'short', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit', hour12: true
                  })}
                </p>
              </div>
            </div>

            <h3 className="text-lg font-semibold mb-3">How's the set?</h3>
            <ReactionButtons onVote={submitRating} stage={selectedEvent.location} />

            {/* Grouped bar */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <div className="text-center flex-1">
                  <div className="text-2xl font-bold text-green-400">{stats.likePct}%</div>
                </div>
                <div className="text-center flex-1">
                  <div className="text-2xl font-bold text-yellow-400">{stats.neuPct}%</div>
                </div>
                <div className="text-center flex-1">
                  <div className="text-2xl font-bold text-red-400">{stats.disPct}%</div>
                </div>
              </div>

              <div className="w-full h-4 bg-gray-700 rounded-full overflow-hidden">
                <div className="flex h-full">
                  <div className="bg-green-500 transition-all" style={{ width: `${stats.likePct}%` }} />
                  <div className="bg-yellow-500 transition-all" style={{ width: `${stats.neuPct}%` }} />
                  <div className="bg-red-500 transition-all" style={{ width: `${stats.disPct}%` }} />
                </div>
              </div>

              <div className="mt-1 text-right text-sm text-gray-400">
                {stats.total} reactions
              </div>

              {/* Top reactions breakdown */}
              {topReactions.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {topReactions.map(r => (
                    <span key={r.key} className="bg-slate-800 px-3 py-1 rounded-full text-sm">
                      {r.emoji} {r.count}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Chart */}
            {chartData.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">Reaction History</h3>
                <div className="h-56 bg-slate-800 p-2 rounded">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                      <XAxis
                        dataKey="time"
                        tickFormatter={(t) => {
                          if (!t || t === 'undefined') return '';
                          const d = new Date(t + 'Z');
                          return d.toLocaleTimeString('en-US', {
                            timeZone: 'America/Los_Angeles',
                            hour: '2-digit', minute: '2-digit', hour12: false,
                          });
                        }}
                        stroke="#aaa"
                      />
                      <YAxis stroke="#aaa" />
                      <Tooltip
                        formatter={(value: number, name: string) => [value, name.charAt(0).toUpperCase() + name.slice(1)]}
                        labelFormatter={(t: string) => `Time: ${String(t).split(' ')[1]}`}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="like" stroke="#10B981" strokeWidth={2} name="Upvote" dot={false} />
                      <Line type="monotone" dataKey="neutral" stroke="#F59E0B" strokeWidth={2} name="Neutral" dot={false} />
                      <Line type="monotone" dataKey="dislike" stroke="#EF4444" strokeWidth={2} name="Downvote" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ALL PERFORMANCES */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">All Performances</h2>

        <div className="flex gap-2 mb-4">
          {[
            { date: '2026-04-17', label: 'Fri W2' },
            { date: '2026-04-18', label: 'Sat W2' },
            { date: '2026-04-19', label: 'Sun W2' },
            { date: '2026-04-10', label: 'Fri W1' },
            { date: '2026-04-11', label: 'Sat W1' },
            { date: '2026-04-12', label: 'Sun W1' },
          ].map(d => (
            <button
              key={d.date}
              className={`px-4 py-2 rounded-t-lg ${
                selectedDay === d.date ? 'bg-cyan-600 text-white font-bold' : 'bg-slate-600 text-gray-200'
              }`}
              onClick={() => setSelectedDay(d.date)}
            >
              {d.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            className={`px-3 py-1 text-sm rounded-full ${selectedStage === 'all' ? 'bg-cyan-600 font-medium' : 'bg-slate-600'}`}
            onClick={() => setSelectedStage('all')}
          >
            All Stages
          </button>
          {uniqueStages.map(stage => (
            <button
              key={stage}
              className={`px-3 py-1 text-sm rounded-full ${selectedStage === stage ? 'bg-cyan-600 font-medium' : 'bg-slate-600'}`}
              onClick={() => setSelectedStage(stage)}
            >
              {stage}
            </button>
          ))}
        </div>

        <div className="mb-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showLate}
              onChange={e => setShowLate(e.target.checked)}
              className="mr-2 h-4 w-4"
            />
            <span>Show only late night sets (after 10pm)</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredEvents.map(event => {
            const startTime = new Date(event.performance_time.replace(' ', 'T') + '-07:00');
            const isPlaying = currentlyPlaying.some(e => e.id === event.id);
            const isUpcoming = startTime.getTime() > currentTime.getTime();

            return (
              <div
                key={event.id}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  isPlaying ? 'bg-slate-700 border-green-500' :
                  isUpcoming ? 'bg-slate-800 border-slate-600 hover:border-slate-400' :
                  'bg-slate-900 border-slate-700 opacity-60'
                }`}
                onClick={() => setSelectedEvent(event)}
              >
                <div className="flex justify-between">
                  <h3 className="font-semibold">{event.name}</h3>
                  {isPlaying && <span className="text-green-400 text-sm">Live now</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm text-gray-400">{event.location}</p>
                  {event.category && <span className="text-xs bg-slate-600 px-1.5 py-0.5 rounded-full">{event.category}</span>}
                  {event.livestream && YT_LINKS[event.location] ? (
                    <a href={YT_LINKS[event.location]} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-xs bg-red-600 hover:bg-red-500 px-1.5 py-0.5 rounded-full font-medium">
                      ▶ LIVE
                    </a>
                  ) : event.livestream ? <span className="text-xs bg-red-600 px-1.5 py-0.5 rounded-full font-medium">LIVE</span> : null}
                </div>
                {event.description && <p className="text-xs text-gray-500 italic">{event.description}</p>}
                <p className="text-xs text-gray-500">
                  {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                </p>
              </div>
            );
          })}
        </div>

        {filteredEvents.length === 0 && (
          <div className="text-center p-8 bg-slate-900 rounded">
            No performances found matching your filters.
          </div>
        )}
      </section>

      <footer className="text-center text-gray-500 text-sm mt-8">
        Couchella Reactions 2026
      </footer>
    </div>
  );
};

export default App;
